<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto text-white" id="style-1">
    <!-- Input and Selection Section -->
    <div class="text-center mb-6">
      <h2 class="text-lg sm:text-xl font-semibold text-indigo-300 mb-3 font-orbitron animate-fadeIn">
        ตรวจสอบสถานะผู้เล่นและอันดับ
      </h2>
      <div class="flex flex-col sm:flex-row justify-center items-center gap-3">
        <div class="flex items-center gap-3">
          <input
            v-model="targetUid"
            type="text"
            placeholder="กรอก UID ผู้เล่นที่ต้องการตรวจสอบ"
            class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
          />
          <input
            v-model="sourceUid"
            type="text"
            placeholder="กรอก UID ผู้เล่นที่ต้องการ copy ค่าพลังจาก"
            class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
          />
          <button
            @click="fetchPlayerData"
            :disabled="isLoading"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading }"
          >
            <div class="btn-content">
              <i class="fas fa-search text-base"></i>
              <span class="text-xs font-semibold">ค้นหา</span>
            </div>
          </button>
        </div>
        <div class="flex items-center gap-3">
          <select
            v-model="selectedCharacter"
            class="bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 w-64"
            @change="fetchPlayerDataByCharacter"
          >
            <option value="">เลือกตัวละครจากอันดับ</option>
            <option v-for="(survivor, index) in survivors" :key="survivor.docId" :value="survivor.docId">
              #{{ index + 1 }} {{ survivor.characterName || 'ไม่ทราบ' }}
            </option>
          </select>
          <button
            @click="previousCharacter"
            :disabled="isLoading || currentIndex <= 0"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex <= 0 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-left text-base"></i>
              <span class="text-xs font-semibold">ก่อนหน้า</span>
            </div>
          </button>
          <button
            @click="nextCharacter"
            :disabled="isLoading || currentIndex >= survivors.length - 1"
            class="btn btn-primary"
            :class="{ 'cursor-not-allowed opacity-70': isLoading || currentIndex >= survivors.length - 1 }"
          >
            <div class="btn-content">
              <i class="fas fa-arrow-right text-base"></i>
              <span class="text-xs font-semibold">ถัดไป</span>
            </div>
          </button>
        </div>
      </div>
      <div v-if="error" class="text-red-400 text-sm mt-3 animate-fadeIn">{{ error }}</div>
    </div>
    <!-- Player Data Display -->
    <div
      v-if="playerData"
      class="bg-gray-700 bg-opacity-90 rounded-lg p-5 border border-gray-600 animate-scaleIn mt-6"
    >
      <h3 class="text-lg font-semibold text-indigo-300 mb-4 font-orbitron">ข้อมูลสถานะผู้เล่น (Target: {{ targetUid }})</h3>
     
      <!-- Copy Stats Button -->
      <div v-if="sourceData" class="mb-6 text-center">
        <button
          @click="copyStats"
          :disabled="isUpdating || !sourceUid"
          class="btn btn-primary"
          :class="{ 'cursor-not-allowed opacity-70': isUpdating || !sourceUid }"
        >
          <div class="btn-content">
            <i class="fas fa-copy text-base"></i>
            <span class="text-xs font-semibold">Copy ค่าพลังจาก Source ({{ sourceUid }})</span>
          </div>
        </button>
        <p class="mt-2 text-xs text-yellow-300">
          จะคัดลอก stats, itemStats, bonusStats, ghostStats, tattooStats, skillStats, titleStats, traitStats ไปยัง Target
        </p>
      </div>
      <!-- Source Data Display -->
      <div v-if="sourceData" class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">ข้อมูล Source ({{ sourceUid }})</h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-indigo-200 text-sm">
          <!-- Source itemStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source itemStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.itemStats.stamina }}</p>
              <p>Strength: {{ sourceData.itemStats.str }}</p>
              <p>Intelligence: {{ sourceData.itemStats.int }}</p>
              <p>Agility: {{ sourceData.itemStats.agi }}</p>
              <p>Luck: {{ sourceData.itemStats.luck }}</p>
              <p>Max HP: {{ sourceData.itemStats.maxHp }}</p>
              <p>Attack: {{ sourceData.itemStats.atk }}</p>
              <p>Evasion: {{ sourceData.itemStats.evasion }}</p>
              <p>Critical: {{ sourceData.itemStats.critical }}</p>
              <p>Defense: {{ sourceData.itemStats.defense }}</p>
            </div>
          </div>
          <!-- Source bonusStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source bonusStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.bonusStats.stamina }}</p>
              <p>Strength: {{ sourceData.bonusStats.str }}</p>
              <p>Intelligence: {{ sourceData.bonusStats.int }}</p>
              <p>Agility: {{ sourceData.bonusStats.agi }}</p>
              <p>Luck: {{ sourceData.bonusStats.luck }}</p>
              <p>Max HP: {{ sourceData.bonusStats.maxHp }}</p>
              <p>Attack: {{ sourceData.bonusStats.atk }}</p>
              <p>Evasion: {{ sourceData.bonusStats.evasion }}</p>
              <p>Critical: {{ sourceData.bonusStats.critical }}</p>
              <p>Defense: {{ sourceData.bonusStats.defense }}</p>
            </div>
          </div>
          <!-- Source stats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source stats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.stats.stamina }}</p>
              <p>Strength: {{ sourceData.stats.str }}</p>
              <p>Intelligence: {{ sourceData.stats.int }}</p>
              <p>Agility: {{ sourceData.stats.agi }}</p>
              <p>Luck: {{ sourceData.stats.luck }}</p>
            </div>
          </div>
          <!-- Source traitStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source traitStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.traitStats.stamina }}</p>
              <p>Strength: {{ sourceData.traitStats.str }}</p>
              <p>Intelligence: {{ sourceData.traitStats.int }}</p>
              <p>Agility: {{ sourceData.traitStats.agi }}</p>
              <p>Luck: {{ sourceData.traitStats.luck }}</p>
            </div>
          </div>
          <!-- Source skillStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source skillStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.skillStats.stamina }}</p>
              <p>Strength: {{ sourceData.skillStats.str }}</p>
              <p>Intelligence: {{ sourceData.skillStats.int }}</p>
              <p>Agility: {{ sourceData.skillStats.agi }}</p>
              <p>Luck: {{ sourceData.skillStats.luck }}</p>
            </div>
          </div>
          <!-- Source tattooStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source tattooStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.tattooStats.stamina }}</p>
              <p>Strength: {{ sourceData.tattooStats.str }}</p>
              <p>Intelligence: {{ sourceData.tattooStats.int }}</p>
              <p>Agility: {{ sourceData.tattooStats.agi }}</p>
              <p>Luck: {{ sourceData.tattooStats.luck }}</p>
            </div>
          </div>
          <!-- Source titleStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source titleStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.titleStats.stamina }}</p>
              <p>Strength: {{ sourceData.titleStats.str }}</p>
              <p>Intelligence: {{ sourceData.titleStats.int }}</p>
              <p>Agility: {{ sourceData.titleStats.agi }}</p>
            </div>
          </div>
          <!-- Source ghostStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h5 class="text-sm font-semibold text-indigo-300 mb-2">Source ghostStats</h5>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ sourceData.ghostStats.stamina }}</p>
              <p>Strength: {{ sourceData.ghostStats.str }}</p>
              <p>Intelligence: {{ sourceData.ghostStats.int }}</p>
              <p>Agility: {{ sourceData.ghostStats.agi }}</p>
              <p>Luck: {{ sourceData.ghostStats.luck }}</p>
              <p>Max HP: {{ sourceData.ghostStats.maxHp }}</p>
              <p>Attack: {{ sourceData.ghostStats.atk }}</p>
              <p>Evasion: {{ sourceData.ghostStats.evasion }}</p>
              <p>Critical: {{ sourceData.ghostStats.critical }}</p>
              <p>Defense: {{ sourceData.ghostStats.defense }}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Level and Stat Points Section -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">ตรวจสอบและอัปเดตเลเวลตัวละคร</h4>
        <div class="text-xs">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">เลเวลปัจจุบัน</h5>
              <p class="text-indigo-200">{{ playerData.level }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">แต้มสถานะปัจจุบัน</h5>
              <p class="text-indigo-200">{{ playerData.statPoints }}</p>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-3">
            <input
              v-model="newLevel"
              type="number"
              min="1"
              placeholder="กรอกเลเวลใหม่"
              class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
            />
            <button
              @click="updateLevel"
              :disabled="isUpdating || !newLevel || newLevel <= 0"
              class="btn btn-primary"
              :class="{ 'cursor-not-allowed opacity-70': isUpdating || !newLevel || newLevel <= 0 }"
            >
              <div class="btn-content">
                <i class="fas fa-level-up-alt text-base"></i>
                <span class="text-xs font-semibold">อัปเดตเลเวล</span>
              </div>
            </button>
          </div>
          <p class="mt-2 text-xs text-yellow-300">
            การอัปเดตเลเวลจะรีเซ็ตสถานะพื้นฐานและให้แต้มสถานะใหม่ตามเลเวล (สูตร: (เลเวล - 1) * 5) และอัปเดตเลเวลใน characters ด้วย
          </p>
        </div>
      </div>
      <!-- Currency Update Section -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">อัปเดตสกุลเงิน</h4>
        <div class="text-xs">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Silver ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ playerData.silver }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Gold ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ playerData.gold }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Diamond ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ playerData.diamond }}</p>
            </div>
          </div>
          <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div class="flex items-center gap-3">
              <input
                v-model="addSilver"
                type="number"
                min="0"
                placeholder="เพิ่ม Silver"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateSilver"
                :disabled="isUpdating || !addSilver || addSilver < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addSilver || addSilver < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Silver</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="addGold"
                type="number"
                min="0"
                placeholder="เพิ่ม Gold"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateGold"
                :disabled="isUpdating || !addGold || addGold < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addGold || addGold < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Gold</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="addDiamond"
                type="number"
                min="0"
                placeholder="เพิ่ม Diamond"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateDiamond"
                :disabled="isUpdating || !addDiamond || addDiamond < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addDiamond || addDiamond < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Diamond</span>
                </div>
              </button>
            </div>
          </div>
          <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div class="flex items-center gap-3">
              <input
                v-model="subtractSilver"
                type="number"
                min="0"
                placeholder="ลด Silver"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="subtractSilverFunc"
                :disabled="isUpdating || !subtractSilver || subtractSilver < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !subtractSilver || subtractSilver < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-minus text-base"></i>
                  <span class="text-xs font-semibold">ลด Silver</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="subtractGold"
                type="number"
                min="0"
                placeholder="ลด Gold"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="subtractGoldFunc"
                :disabled="isUpdating || !subtractGold || subtractGold < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !subtractGold || subtractGold < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-minus text-base"></i>
                  <span class="text-xs font-semibold">ลด Gold</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="subtractDiamond"
                type="number"
                min="0"
                placeholder="ลด Diamond"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="subtractDiamondFunc"
                :disabled="isUpdating || !subtractDiamond || subtractDiamond < 0"
                class="btn btn-primary"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !subtractDiamond || subtractDiamond < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-minus text-base"></i>
                  <span class="text-xs font-semibold">ลด Diamond</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Item Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Item Stats</h4>
        <div class="text-xs space-y-3">
          <!-- Row 1: Normal Equipped + Mechanical -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Slot 1: Normal Equipped Items -->
            <div class="bg-gray-800 p-3 rounded-md border border-indigo-700">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-semibold text-indigo-300 text-sm">
                  <i class="fas fa-shirt mr-1"></i>อุปกรณ์ที่สวมใส่ ({{ items.length }} ชิ้น)
                </h5>
                <span class="text-xxs px-2 py-0.5 rounded bg-indigo-900 text-indigo-200 border border-indigo-600">Equipped</span>
              </div>
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5 text-indigo-200">
                <p>Stamina: <span class="font-mono">{{ calculatedNormalItemStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ calculatedNormalItemStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ calculatedNormalItemStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ calculatedNormalItemStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ calculatedNormalItemStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ calculatedNormalItemStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ calculatedNormalItemStats.atk }}</span></p>
                <p>Evasion: <span class="font-mono">{{ calculatedNormalItemStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ calculatedNormalItemStats.critical }}</span></p>
                <p>Defense: <span class="font-mono">{{ calculatedNormalItemStats.defense }}</span></p>
              </div>
            </div>
            <!-- Slot 2: Mechanical Items -->
            <div class="bg-gray-800 p-3 rounded-md border border-orange-700">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-semibold text-orange-300 text-sm">
                  <i class="fas fa-cogs mr-1"></i>อวัยวะจักรกล ({{ mechanicalItems.length }} ชิ้น)
                </h5>
                <span class="text-xxs px-2 py-0.5 rounded bg-orange-900 text-orange-200 border border-orange-600">Mechanical</span>
              </div>
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5 text-orange-200">
                <p>Stamina: <span class="font-mono">{{ calculatedMechanicalItemStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ calculatedMechanicalItemStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ calculatedMechanicalItemStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ calculatedMechanicalItemStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ calculatedMechanicalItemStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ calculatedMechanicalItemStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ calculatedMechanicalItemStats.atk }}</span></p>
                <p>Evasion: <span class="font-mono">{{ calculatedMechanicalItemStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ calculatedMechanicalItemStats.critical }}</span></p>
                <p>Defense: <span class="font-mono">{{ calculatedMechanicalItemStats.defense }}</span></p>
              </div>
            </div>
          </div>

          <!-- Row 2: Total Calculated + Stored + Comparison -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- Slot 3: Total Calculated -->
            <div class="bg-gray-800 p-3 rounded-md border border-green-700">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-semibold text-green-300 text-sm">
                  <i class="fas fa-calculator mr-1"></i>คำนวณรวม (1+2)
                </h5>
                <span class="text-xxs px-2 py-0.5 rounded bg-green-900 text-green-200 border border-green-600">Calculated</span>
              </div>
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5 text-green-200">
                <p>Stamina: <span class="font-mono">{{ calculatedItemStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ calculatedItemStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ calculatedItemStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ calculatedItemStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ calculatedItemStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ calculatedItemStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ calculatedItemStats.atk }}</span></p>
                <p>Evasion: <span class="font-mono">{{ calculatedItemStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ calculatedItemStats.critical }}</span></p>
                <p>Defense: <span class="font-mono">{{ calculatedItemStats.defense }}</span></p>
              </div>
            </div>
            <!-- Slot 4: Stored itemStats -->
            <div class="bg-gray-800 p-3 rounded-md border border-blue-700">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-semibold text-blue-300 text-sm">
                  <i class="fas fa-database mr-1"></i>เก็บไว้ในระบบ
                </h5>
                <span class="text-xxs px-2 py-0.5 rounded bg-blue-900 text-blue-200 border border-blue-600">Stored</span>
              </div>
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5 text-blue-200">
                <p>Stamina: <span class="font-mono">{{ playerData.itemStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ playerData.itemStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ playerData.itemStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ playerData.itemStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ playerData.itemStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ playerData.itemStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ playerData.itemStats.atk }}</span></p>
                <p>Evasion: <span class="font-mono">{{ playerData.itemStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ playerData.itemStats.critical }}</span></p>
                <p>Defense: <span class="font-mono">{{ playerData.itemStats.defense }}</span></p>
              </div>
            </div>
            <!-- Slot 5: Comparison -->
            <div class="bg-gray-800 p-3 rounded-md border"
              :class="isItemStatsMatching ? 'border-green-600' : 'border-red-600'">
              <div class="flex items-center justify-between mb-2">
                <h5 class="font-semibold text-sm" :class="isItemStatsMatching ? 'text-green-300' : 'text-red-300'">
                  <i class="fas mr-1" :class="isItemStatsMatching ? 'fa-check-circle' : 'fa-times-circle'"></i>
                  เปรียบเทียบ (3 vs 4)
                </h5>
                <span class="text-xxs px-2 py-0.5 rounded border"
                  :class="isItemStatsMatching ? 'bg-green-900 text-green-200 border-green-600' : 'bg-red-900 text-red-200 border-red-600'">
                  {{ isItemStatsMatching ? 'ตรงกัน' : 'ไม่ตรงกัน' }}
                </span>
              </div>
              <div class="grid grid-cols-2 gap-x-2 gap-y-0.5"
                :class="isItemStatsMatching ? 'text-green-200' : 'text-red-200'">
                <p>Stamina: <span class="font-mono">{{ calculatedItemStats.stamina - playerData.itemStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ calculatedItemStats.str - playerData.itemStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ calculatedItemStats.int - playerData.itemStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ calculatedItemStats.agi - playerData.itemStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ calculatedItemStats.luck - playerData.itemStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ calculatedItemStats.maxHp - playerData.itemStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ calculatedItemStats.atk - playerData.itemStats.atk }}</span></p>
                <p>Evasion: <span class="font-mono">{{ calculatedItemStats.evasion - playerData.itemStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ calculatedItemStats.critical - playerData.itemStats.critical }}</span></p>
                <p>Defense: <span class="font-mono">{{ calculatedItemStats.defense - playerData.itemStats.defense }}</span></p>
              </div>
              <p v-if="!isItemStatsMatching" class="mt-2 text-xxs text-red-300">
                <i class="fas fa-exclamation-triangle mr-1"></i>ค่าต่าง ≠ 0 แสดงว่ามีความไม่ตรงกัน
              </p>
            </div>
          </div>

          <button
            v-if="!isItemStatsMatching"
            @click="updateItemStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-2"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Item Stats ให้ตรงกับคำนวณ</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Ghost Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Ghost Stats กับ Ghost Items</h4>
        <div class="text-xs">
          <p class="text-indigo-200">จำนวนวิญญาณ: {{ ghostItems.length }}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Ghost Stats (จาก characterPrivate)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ playerData.ghostStats.stamina }}</p>
                <p>Strength: {{ playerData.ghostStats.str }}</p>
                <p>Intelligence: {{ playerData.ghostStats.int }}</p>
                <p>Agility: {{ playerData.ghostStats.agi }}</p>
                <p>Luck: {{ playerData.ghostStats.luck }}</p>
                <p>Max HP: {{ playerData.ghostStats.maxHp }}</p>
                <p>Attack: {{ playerData.ghostStats.atk }}</p>
                <p>Evasion: {{ playerData.ghostStats.evasion }}</p>
                <p>Critical: {{ playerData.ghostStats.critical }}</p>
                <p>Defense: {{ playerData.ghostStats.defense }}</p>
              </div>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Calculated Ghost Stats (จาก Ghost Items)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ calculatedGhostStats.stamina }}</p>
                <p>Strength: {{ calculatedGhostStats.str }}</p>
                <p>Intelligence: {{ calculatedGhostStats.int }}</p>
                <p>Agility: {{ calculatedGhostStats.agi }}</p>
                <p>Luck: {{ calculatedGhostStats.luck }}</p>
                <p>Max HP: {{ calculatedGhostStats.maxHp }}</p>
                <p>Attack: {{ calculatedGhostStats.atk }}</p>
                <p>Evasion: {{ calculatedGhostStats.evasion }}</p>
                <p>Critical: {{ calculatedGhostStats.critical }}</p>
                <p>Defense: {{ calculatedGhostStats.defense }}</p>
              </div>
            </div>
          </div>
          <p
            class="mt-3 text-sm"
            :class="isGhostStatsMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isGhostStatsMatching ? 'สถานะวิญญาณตรงกัน' : 'สถานะวิญญาณไม่ตรงกัน' }}
          </p>
          <button
            v-if="!isGhostStatsMatching"
            @click="updateGhostStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Ghost Stats</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Tattoo Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Tattoo Stats กับ Tattoos</h4>
        <div class="text-xs">
          <p class="text-indigo-200">จำนวนรอยสัก: {{ tattoos.length }}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Tattoo Stats (จาก characterPrivate)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ playerData.tattooStats.stamina }}</p>
                <p>Strength: {{ playerData.tattooStats.str }}</p>
                <p>Intelligence: {{ playerData.tattooStats.int }}</p>
                <p>Agility: {{ playerData.tattooStats.agi }}</p>
                <p>Luck: {{ playerData.tattooStats.luck }}</p>
              </div>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Calculated Tattoo Stats (จาก Tattoos)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ calculatedTattooStats.stamina }}</p>
                <p>Strength: {{ calculatedTattooStats.str }}</p>
                <p>Intelligence: {{ calculatedTattooStats.int }}</p>
                <p>Agility: {{ calculatedTattooStats.agi }}</p>
                <p>Luck: {{ calculatedTattooStats.luck }}</p>
              </div>
            </div>
          </div>
          <p
            class="mt-3 text-sm"
            :class="isTattooStatsMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isTattooStatsMatching ? 'สถานะรอยสักตรงกัน' : 'สถานะรอยสักไม่ตรงกัน' }}
          </p>
          <button
            v-if="!isTattooStatsMatching"
            @click="updateTattooStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Tattoo Stats</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Skill Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Skill Stats กับ Learned Skills</h4>
        <div class="text-xs">
          <p class="text-indigo-200">จำนวนสกิล: {{ skills.length }}</p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Skill Stats (จาก characterPrivate)</h5>
              <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-indigo-200">
                <p>Stamina: <span class="font-mono">{{ playerData.skillStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ playerData.skillStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ playerData.skillStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ playerData.skillStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ playerData.skillStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ playerData.skillStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ playerData.skillStats.atk }}</span></p>
                <p>Defense: <span class="font-mono">{{ playerData.skillStats.defense }}</span></p>
                <p>Evasion: <span class="font-mono">{{ playerData.skillStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ playerData.skillStats.critical }}</span></p>
              </div>
            </div>

            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Calculated Skill Stats (จาก Learned Skills)</h5>
              <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-indigo-200">
                <p>Stamina: <span class="font-mono">{{ calculatedSkillStats.stamina }}</span></p>
                <p>Strength: <span class="font-mono">{{ calculatedSkillStats.str }}</span></p>
                <p>Intelligence: <span class="font-mono">{{ calculatedSkillStats.int }}</span></p>
                <p>Agility: <span class="font-mono">{{ calculatedSkillStats.agi }}</span></p>
                <p>Luck: <span class="font-mono">{{ calculatedSkillStats.luck }}</span></p>
                <p>Max HP: <span class="font-mono">{{ calculatedSkillStats.maxHp }}</span></p>
                <p>Attack: <span class="font-mono">{{ calculatedSkillStats.atk }}</span></p>
                <p>Defense: <span class="font-mono">{{ calculatedSkillStats.defense }}</span></p>
                <p>Evasion: <span class="font-mono">{{ calculatedSkillStats.evasion }}</span></p>
                <p>Critical: <span class="font-mono">{{ calculatedSkillStats.critical }}</span></p>
              </div>
            </div>
          </div>

          <div
            class="mt-3 p-3 rounded-md border"
            :class="isSkillStatsMatching ? 'border-green-600 bg-green-900 bg-opacity-30' : 'border-red-600 bg-red-900 bg-opacity-30'"
          >
            <p
              class="text-sm font-semibold"
              :class="isSkillStatsMatching ? 'text-green-400' : 'text-red-400'"
            >
              {{ isSkillStatsMatching ? 'สถานะสกิลตรงกัน' : 'สถานะสกิลไม่ตรงกัน' }}
            </p>

            <div
              v-if="!isSkillStatsMatching"
              class="grid grid-cols-2 sm:grid-cols-5 gap-x-3 gap-y-1 mt-2 text-red-200"
            >
              <p>Stamina: <span class="font-mono">{{ calculatedSkillStats.stamina - playerData.skillStats.stamina }}</span></p>
              <p>Strength: <span class="font-mono">{{ calculatedSkillStats.str - playerData.skillStats.str }}</span></p>
              <p>Intelligence: <span class="font-mono">{{ calculatedSkillStats.int - playerData.skillStats.int }}</span></p>
              <p>Agility: <span class="font-mono">{{ calculatedSkillStats.agi - playerData.skillStats.agi }}</span></p>
              <p>Luck: <span class="font-mono">{{ calculatedSkillStats.luck - playerData.skillStats.luck }}</span></p>
              <p>Max HP: <span class="font-mono">{{ calculatedSkillStats.maxHp - playerData.skillStats.maxHp }}</span></p>
              <p>Attack: <span class="font-mono">{{ calculatedSkillStats.atk - playerData.skillStats.atk }}</span></p>
              <p>Defense: <span class="font-mono">{{ calculatedSkillStats.defense - playerData.skillStats.defense }}</span></p>
              <p>Evasion: <span class="font-mono">{{ calculatedSkillStats.evasion - playerData.skillStats.evasion }}</span></p>
              <p>Critical: <span class="font-mono">{{ calculatedSkillStats.critical - playerData.skillStats.critical }}</span></p>
            </div>
          </div>

          <button
            v-if="!isSkillStatsMatching"
            @click="updateSkillStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Skill Stats</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Title Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Title Stats กับ Current Title</h4>
        <div class="text-xs">
          <p class="text-indigo-200">ฉายาปัจจุบัน: {{ currentTitle || 'ไม่มีฉายา' }}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Title Stats (จาก characterPrivate)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ playerData.titleStats.stamina }}</p>
                <p>Strength: {{ playerData.titleStats.str }}</p>
                <p>Intelligence: {{ playerData.titleStats.int }}</p>
                <p>Agility: {{ playerData.titleStats.agi }}</p>
              </div>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Calculated Title Stats (จาก Current Title)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ calculatedTitleStats.stamina }}</p>
                <p>Strength: {{ calculatedTitleStats.str }}</p>
                <p>Intelligence: {{ calculatedTitleStats.int }}</p>
                <p>Agility: {{ calculatedTitleStats.agi }}</p>
              </div>
            </div>
          </div>
          <p
            class="mt-3 text-sm"
            :class="isTitleStatsMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isTitleStatsMatching ? 'สถานะฉายาตรงกัน' : 'สถานะฉายาไม่ตรงกัน' }}
          </p>
          <button
            v-if="!isTitleStatsMatching"
            @click="updateTitleStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Title Stats</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Set Bonus Stats Comparison -->
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Bonus Stats กับ Set Items</h4>
        <div class="text-xs">
          <p class="text-indigo-200">จำนวนไอเทมในเซ็ตที่สวมใส่: {{ itemNames.length }}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Bonus Stats (จาก characterPrivate)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ playerData.bonusStats.stamina }}</p>
                <p>Strength: {{ playerData.bonusStats.str }}</p>
                <p>Intelligence: {{ playerData.bonusStats.int }}</p>
                <p>Agility: {{ playerData.bonusStats.agi }}</p>
                <p>Luck: {{ playerData.bonusStats.luck }}</p>
                <p>Max HP: {{ playerData.bonusStats.maxHp }}</p>
                <p>Attack: {{ playerData.bonusStats.atk }}</p>
                <p>Evasion: {{ playerData.bonusStats.evasion }}</p>
                <p>Critical: {{ playerData.bonusStats.critical }}</p>
                <p>Defense: {{ playerData.bonusStats.defense }}</p>
              </div>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Calculated Bonus Stats (จาก Set Items)</h5>
              <div class="text-indigo-200 space-y-1">
                <p>Stamina: {{ calculatedBonusStats.stamina }}</p>
                <p>Strength: {{ calculatedBonusStats.str }}</p>
                <p>Intelligence: {{ calculatedBonusStats.int }}</p>
                <p>Agility: {{ calculatedBonusStats.agi }}</p>
                <p>Luck: {{ calculatedBonusStats.luck }}</p>
                <p>Max HP: {{ calculatedBonusStats.maxHp }}</p>
                <p>Attack: {{ calculatedBonusStats.atk }}</p>
                <p>Evasion: {{ calculatedBonusStats.evasion }}</p>
                <p>Critical: {{ calculatedBonusStats.critical }}</p>
                <p>Defense: {{ calculatedBonusStats.defense }}</p>
              </div>
            </div>
          </div>
          <p
            class="mt-3 text-sm"
            :class="isBonusStatsMatching ? 'text-green-400' : 'text-red-400'"
          >
            {{ isBonusStatsMatching ? 'สถานะโบนัสเซ็ตตรงกัน' : 'สถานะโบนัสเซ็ตไม่ตรงกัน' }}
          </p>
          <button
            v-if="!isBonusStatsMatching"
            @click="updateBonusStats"
            :disabled="isUpdating"
            class="btn btn-primary mt-3"
            :class="{ 'cursor-not-allowed opacity-70': isUpdating }"
          >
            <div class="btn-content">
              <i class="fas fa-sync-alt text-base"></i>
              <span class="text-xs font-semibold">อัปเดต Bonus Stats</span>
            </div>
          </button>
        </div>
      </div>
      <!-- Individual Stats -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-indigo-200 text-sm">
        <!-- itemStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">itemStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.itemStats.stamina }}</p>
            <p>Strength: {{ playerData.itemStats.str }}</p>
            <p>Intelligence: {{ playerData.itemStats.int }}</p>
            <p>Agility: {{ playerData.itemStats.agi }}</p>
            <p>Luck: {{ playerData.itemStats.luck }}</p>
            <p>Max HP: {{ playerData.itemStats.maxHp }}</p>
            <p>Attack: {{ playerData.itemStats.atk }}</p>
            <p>Evasion: {{ playerData.itemStats.evasion }}</p>
            <p>Critical: {{ playerData.itemStats.critical }}</p>
            <p>Defense: {{ playerData.itemStats.defense }}</p>
          </div>
        </div>
        <!-- bonusStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">bonusStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.bonusStats.stamina }}</p>
            <p>Strength: {{ playerData.bonusStats.str }}</p>
            <p>Intelligence: {{ playerData.bonusStats.int }}</p>
            <p>Agility: {{ playerData.bonusStats.agi }}</p>
            <p>Luck: {{ playerData.bonusStats.luck }}</p>
            <p>Max HP: {{ playerData.bonusStats.maxHp }}</p>
            <p>Attack: {{ playerData.bonusStats.atk }}</p>
            <p>Evasion: {{ playerData.bonusStats.evasion }}</p>
            <p>Critical: {{ playerData.bonusStats.critical }}</p>
            <p>Defense: {{ playerData.bonusStats.defense }}</p>
          </div>
        </div>
        <!-- stats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">stats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.stats.stamina }}</p>
            <p>Strength: {{ playerData.stats.str }}</p>
            <p>Intelligence: {{ playerData.stats.int }}</p>
            <p>Agility: {{ playerData.stats.agi }}</p>
            <p>Luck: {{ playerData.stats.luck }}</p>
          </div>
        </div>
        <!-- traitStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">traitStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.traitStats.stamina }}</p>
            <p>Strength: {{ playerData.traitStats.str }}</p>
            <p>Intelligence: {{ playerData.traitStats.int }}</p>
            <p>Agility: {{ playerData.traitStats.agi }}</p>
            <p>Luck: {{ playerData.traitStats.luck }}</p>
          </div>
        </div>
        <!-- skillStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">skillStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.skillStats.stamina }}</p>
            <p>Strength: {{ playerData.skillStats.str }}</p>
            <p>Intelligence: {{ playerData.skillStats.int }}</p>
            <p>Agility: {{ playerData.skillStats.agi }}</p>
            <p>Luck: {{ playerData.skillStats.luck }}</p>
          </div>
        </div>
        <!-- tattooStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">tattooStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.tattooStats.stamina }}</p>
            <p>Strength: {{ playerData.tattooStats.str }}</p>
            <p>Intelligence: {{ playerData.tattooStats.int }}</p>
            <p>Agility: {{ playerData.tattooStats.agi }}</p>
            <p>Luck: {{ playerData.tattooStats.luck }}</p>
          </div>
        </div>
        <!-- titleStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">titleStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.titleStats.stamina }}</p>
            <p>Strength: {{ playerData.titleStats.str }}</p>
            <p>Intelligence: {{ playerData.titleStats.int }}</p>
            <p>Agility: {{ playerData.titleStats.agi }}</p>
          </div>
        </div>
        <!-- ghostStats -->
        <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
          <h4 class="text-sm font-semibold text-indigo-300 mb-2">ghostStats</h4>
          <div class="text-xs space-y-1">
            <p>Stamina: {{ playerData.ghostStats.stamina }}</p>
            <p>Strength: {{ playerData.ghostStats.str }}</p>
            <p>Intelligence: {{ playerData.ghostStats.int }}</p>
            <p>Agility: {{ playerData.ghostStats.agi }}</p>
            <p>Luck: {{ playerData.ghostStats.luck }}</p>
            <p>Max HP: {{ playerData.ghostStats.maxHp }}</p>
            <p>Attack: {{ playerData.ghostStats.atk }}</p>
            <p>Evasion: {{ playerData.ghostStats.evasion }}</p>
            <p>Critical: {{ playerData.ghostStats.critical }}</p>
            <p>Defense: {{ playerData.ghostStats.defense }}</p>
          </div>
        </div>
      </div>
    </div>
    <!-- No Data -->
    <div
      v-else-if="!isLoading && !playerData"
      class="text-center text-indigo-300 text-sm animate-fadeIn"
    >
      กรุณากรอก UID หรือเลือกตัวละครเพื่อตรวจสอบข้อมูล
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted } from 'vue';
import { projectFirestore } from '../firebase/config';
import gameItemsData from '@/data/gameItems.json';
import firebase from 'firebase/app';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'firebase/firestore';
dayjs.extend(relativeTime);
dayjs.locale('th');
export default {
  setup() {
    const targetUid = ref(''); // UID สำหรับตรวจสอบ (เดิมคือ uid)
    const sourceUid = ref(''); // UID สำหรับ copy จาก
    const selectedCharacter = ref('');
    const playerData = ref(null); // Target data
    const sourceData = ref(null); // Source data
    const items = ref([]);
    const mechanicalItems = ref([]);
    const tattoos = ref([]);
    const skills = ref([]);
    const ghostItems = ref([]);
    const survivors = ref([]);
    const error = ref(null);
    const isLoading = ref(false);
    const isUpdating = ref(false);
    const isLoadingMore = ref(false);
    const lastDoc = ref(null);
    const hasMoreData = ref(true);
    const pageSize = 150;
    const currentIndex = ref(-1);
    const newLevel = ref(null);
    const addSilver = ref(null);
    const addGold = ref(null);
    const addDiamond = ref(null);
    const subtractSilver = ref(null);
    const subtractGold = ref(null);
    const subtractDiamond = ref(null);
    const itemNames = ref([]);
    const currentTitle = ref(null);
    const STAT_KEYS = ['stamina', 'str', 'int', 'agi', 'luck', 'maxHp', 'atk', 'evasion', 'critical', 'defense'];
    const BASE_STAT_KEYS = ['stamina', 'str', 'int', 'agi', 'luck'];

    const toNumber = (value) => Number(value) || 0;

    const emptyStats = (keys = STAT_KEYS) => keys.reduce((result, key) => {
      result[key] = 0;
      return result;
    }, {});

    const normalizeStats = (stats = {}, keys = STAT_KEYS) => {
      const source = stats && typeof stats === 'object' ? stats : {};
      return keys.reduce((result, key) => {
        result[key] = toNumber(source[key]);
        return result;
      }, {});
    };

    const addStats = (target, source = {}, keys = STAT_KEYS) => {
      const safeSource = source && typeof source === 'object' ? source : {};
      keys.forEach((key) => {
        target[key] = toNumber(target[key]) + toNumber(safeSource[key]);
      });
      return target;
    };

    const areStatsEqual = (a = {}, b = {}, keys = STAT_KEYS) => {
      const left = normalizeStats(a, keys);
      const right = normalizeStats(b, keys);
      return keys.every((key) => left[key] === right[key]);
    };

    const findGameItemData = (idOrName) => {
      if (!idOrName) return null;
      if (gameItemsData[idOrName]) {
        return { id: idOrName, ...gameItemsData[idOrName] };
      }
      const entries = Array.isArray(gameItemsData)
        ? gameItemsData.map((item, index) => [item.id || item.itemId || String(index), item])
        : Object.entries(gameItemsData);
      const found = entries.find(([id, item]) => {
        return id === idOrName || item?.id === idOrName || item?.itemId === idOrName || item?.name === idOrName;
      });
      return found ? { id: found[0], ...found[1] } : null;
    };

    // Titles configuration
    const titles = {
      'เด็กเก็บขยะ': {
        stats: { stamina: 3, str: 0, int: 0, agi: 0 },
        renown: 300,
        goldCost: 300,
        silverCost: 3000,
        levelRequired: 3
      },
      'เด็กส่งของ': {
        stats: { stamina: 3, str: 3, int: 0, agi: 0 },
        renown: 600,
        goldCost: 600,
        silverCost: 6000,
        levelRequired: 6,
        requiredTitle: 'เด็กเก็บขยะ'
      },
      'คนรับใช้': {
        stats: { stamina: 3, str: 3, int: 3, agi: 0 },
        renown: 900,
        goldCost: 900,
        silverCost: 9000,
        levelRequired: 9,
        requiredTitle: 'เด็กส่งของ'
      },
      'คนสอดรู้': {
        stats: { stamina: 3, str: 3, int: 3, agi: 3 },
        renown: 1200,
        goldCost: 1200,
        silverCost: 12000,
        levelRequired: 12,
        requiredTitle: 'คนรับใช้'
      },
      'ช่างซ่อม': {
        stats: { stamina: 6, str: 3, int: 3, agi: 3 },
        renown: 1500,
        goldCost: 1500,
        silverCost: 15000,
        levelRequired: 15,
        requiredTitle: 'คนสอดรู้'
      },
      'นักสำรวจ': {
        stats: { stamina: 6, str: 6, int: 3, agi: 3 },
        renown: 1800,
        goldCost: 1800,
        silverCost: 18000,
        levelRequired: 18,
        requiredTitle: 'ช่างซ่อม'
      },
      'นักวางแผน': {
        stats: { stamina: 6, str: 6, int: 6, agi: 6 },
        renown: 2100,
        goldCost: 2100,
        silverCost: 21000,
        levelRequired: 21,
        requiredTitle: 'นักสำรวจ'
      },
      'ผู้มีประสบการณ์': {
        stats: { stamina: 9, str: 6, int: 6, agi: 6 },
        renown: 2400,
        goldCost: 2400,
        silverCost: 24000,
        levelRequired: 24,
        requiredTitle: 'นักวางแผน'
      },
      'ผู้มีอิทธิพล': {
        stats: { stamina: 9, str: 9, int: 9, agi: 6 },
        renown: 2700,
        goldCost: 2700,
        silverCost: 27000,
        levelRequired: 27,
        requiredTitle: 'ผู้มีประสบการณ์'
      },
      'เจ้าพ่อ': {
        stats: { stamina: 10, str: 10, int: 10, agi: 10 },
        renown: 3000,
        goldCost: 3000,
        silverCost: 30000,
        levelRequired: 30,
        requiredTitle: 'ผู้มีอิทธิพล'
      },
      'คนครึ่งหุ่นยนต์': {
        stats: { stamina: 13, str: 10, int: 10, agi: 10 },
        renown: 3300,
        goldCost: 3500,
        silverCost: 40000,
        levelRequired: 30,
        requiredTitle: 'เจ้าพ่อ'
      },
      'คนเล่นของ': {
        stats: { stamina: 13, str: 13, int: 10, agi: 10 },
        renown: 3600,
        goldCost: 4000,
        silverCost: 50000,
        levelRequired: 30,
        requiredTitle: 'คนครึ่งหุ่นยนต์'
      },
      'คนประหลาด': {
        stats: { stamina: 13, str: 13, int: 13, agi: 10 },
        renown: 3900,
        goldCost: 4500,
        silverCost: 60000,
        levelRequired: 30,
        requiredTitle: 'คนเล่นของ'
      },
      'มนุษย์เหล็ก': {
        stats: { stamina: 13, str: 13, int: 13, agi: 13 },
        renown: 4200,
        goldCost: 5000,
        silverCost: 70000,
        levelRequired: 30,
        requiredTitle: 'คนประหลาด'
      },
      'นักรบเหล็ก': {
        stats: { stamina: 16, str: 13, int: 13, agi: 13 },
        renown: 4500,
        goldCost: 5500,
        silverCost: 70000,
        levelRequired: 30,
        requiredTitle: 'มนุษย์เหล็ก'
      },
      'ผู้ทรงพลัง': {
        stats: { stamina: 16, str: 16, int: 13, agi: 13 },
        renown: 4800,
        goldCost: 6000,
        silverCost: 90000,
        levelRequired: 30,
        requiredTitle: 'นักรบเหล็ก'
      }
    };
    // Set configurations (mirroring server-side)
    const setConfigs = {
      'เซ็ตทหาร': {
        items: [
          'หมวกทหาร', 'เสื้อกองทัพบก', 'เสื้อคลุมทหาร', 'ถุงมือทหาร',
          'กางเกงทหาร', 'รองเท้าคอมแบท', 'เข็มขัดทหาร', 'แท่งเหล็กปลายมีด'
        ],
        bonuses: {
          4: { critical: 4, evasion: 4, atk: 20 },
          8: { critical: 10, evasion: 10, atk: 50 }
        }
      },
      'เซ็ตนักดับเพลิง': {
        items: [
          'หมวกนิรภัย', 'เสื้อนักดับเพลิง', 'เสื้อคลุมนักดับเพลิง', 'ถุงมือนักดับเพลิง',
          'กางเกงนักดับเพลิง', 'รองเท้านักดับเพลิง', 'เข็มขัดนักดับเพลิง', 'เลื่อยไฟฟ้า'
        ],
        bonuses: {
          4: { maxHp: 1000, critical: 4, defense: 20 },
          8: { maxHp: 2500, critical: 10, defense: 50, atk: 10 }
        }
      },
      'เซ็ตตัวตลก': {
        items: ['ถุงมือตัวตลก', 'เสื้อตัวตลก', 'กางเกงตัวตลก', 'หมวกตัวตลก', 'รองเท้าตัวตลก'],
        bonuses: {
          2: { atk: 15, critical: 10 },
          5: { atk: 40, critical: 20 }
        }
      },
      'เซ็ตเทควันโด้': {
        items: ['เสื้อเทควันโด', 'เสื้อคลุมเทควันโด', 'กางเกงเทควันโด'],
        bonuses: {
          3: { evasion: 20, atk: 30 }
        }
      },
      'เซ็นนักรบไทย': {
        items: ['เกราะนักรบไทย', 'ปลอกแขนนักรบไทย', 'สนับแข้งนักรบไทย','หมวกนักรบไทย','หมวกอัศวิน','ดาบ'],
        bonuses: {
          3: { critical: 3, evasion: 3, atk: 20},
          4: { critical: 6, evasion: 6, atk: 30},
          5: { critical: 10, evasion: 10, atk: 40}
        }
      }
    };
    // Computed property for calculated bonus stats
    const calculatedBonusStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0,
        atk: 0, evasion: 0, critical: 0, defense: 0
      };
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedInSet = itemNames.value.filter(name => config.items.includes(name)).length;
        if (equippedInSet === 0) continue;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => b - a);
        for (const required of bonusKeys) {
          if (equippedInSet >= required) {
            const bonus = config.bonuses[required];
            for (const [stat, value] of Object.entries(bonus)) {
              stats[stat] = (stats[stat] || 0) + value;
            }
            break;
          }
        }
      }
      return stats;
    });
    // Computed property to check if bonusStats matches calculated bonus stats
    const isBonusStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.bonusStats) return true;
      const bonusStats = playerData.value.bonusStats;
      return (
        bonusStats.stamina === calculatedBonusStats.value.stamina &&
        bonusStats.str === calculatedBonusStats.value.str &&
        bonusStats.int === calculatedBonusStats.value.int &&
        bonusStats.agi === calculatedBonusStats.value.agi &&
        bonusStats.luck === calculatedBonusStats.value.luck &&
        bonusStats.maxHp === calculatedBonusStats.value.maxHp &&
        bonusStats.atk === calculatedBonusStats.value.atk &&
        bonusStats.evasion === calculatedBonusStats.value.evasion &&
        bonusStats.critical === calculatedBonusStats.value.critical &&
        bonusStats.defense === calculatedBonusStats.value.defense
      );
    });
    // Computed property for calculated normal item stats
    const calculatedNormalItemStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      items.value.forEach((item) => {
        const itemStats = item.stats || {};
        const upgradeStats = item.upgradeStats || {};
        const cardStats = item.cardStats || {};
        stats.stamina += toNumber(itemStats.stamina) + toNumber(upgradeStats.stamina) + toNumber(cardStats.stamina);
        stats.str += toNumber(itemStats.str) + toNumber(upgradeStats.str) + toNumber(cardStats.str);
        stats.int += toNumber(itemStats.int) + toNumber(upgradeStats.int) + toNumber(cardStats.int);
        stats.agi += toNumber(itemStats.agi) + toNumber(upgradeStats.agi) + toNumber(cardStats.agi);
        stats.luck += toNumber(itemStats.luck) + toNumber(upgradeStats.luck) + toNumber(cardStats.luck);
        stats.maxHp += toNumber(itemStats.maxHp) + toNumber(upgradeStats.maxHp) + toNumber(cardStats.maxHp);
        stats.atk += toNumber(itemStats.atk) + toNumber(upgradeStats.atk) + toNumber(cardStats.atk);
        stats.evasion += toNumber(itemStats.evasion) + toNumber(upgradeStats.evasion) + toNumber(cardStats.evasion);
        stats.critical += toNumber(itemStats.critical) + toNumber(upgradeStats.critical) + toNumber(cardStats.critical);
        stats.defense += toNumber(itemStats.defense) + toNumber(upgradeStats.defense) + toNumber(cardStats.defense);
      });
      return stats;
    });
    // Computed property for calculated mechanical item stats
    const calculatedMechanicalItemStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      mechanicalItems.value.forEach((item) => {
        const itemStats = item.stats || {};
        const upgradeStats = item.upgradeStats || {};
        const cardStats = item.cardStats || {};
        stats.stamina += toNumber(itemStats.stamina) + toNumber(upgradeStats.stamina) + toNumber(cardStats.stamina);
        stats.str += toNumber(itemStats.str) + toNumber(upgradeStats.str) + toNumber(cardStats.str);
        stats.int += toNumber(itemStats.int) + toNumber(upgradeStats.int) + toNumber(cardStats.int);
        stats.agi += toNumber(itemStats.agi) + toNumber(upgradeStats.agi) + toNumber(cardStats.agi);
        stats.luck += toNumber(itemStats.luck) + toNumber(upgradeStats.luck) + toNumber(cardStats.luck);
        stats.maxHp += toNumber(itemStats.maxHp) + toNumber(upgradeStats.maxHp) + toNumber(cardStats.maxHp);
        stats.atk += toNumber(itemStats.atk) + toNumber(upgradeStats.atk) + toNumber(cardStats.atk);
        stats.evasion += toNumber(itemStats.evasion) + toNumber(upgradeStats.evasion) + toNumber(cardStats.evasion);
        stats.critical += toNumber(itemStats.critical) + toNumber(upgradeStats.critical) + toNumber(cardStats.critical);
        stats.defense += toNumber(itemStats.defense) + toNumber(upgradeStats.defense) + toNumber(cardStats.defense);
      });
      return stats;
    });
    // Computed property for calculated total item stats
    const calculatedItemStats = computed(() => {
      const stats = {
        stamina: calculatedNormalItemStats.value.stamina + calculatedMechanicalItemStats.value.stamina,
        str: calculatedNormalItemStats.value.str + calculatedMechanicalItemStats.value.str,
        int: calculatedNormalItemStats.value.int + calculatedMechanicalItemStats.value.int,
        agi: calculatedNormalItemStats.value.agi + calculatedMechanicalItemStats.value.agi,
        luck: calculatedNormalItemStats.value.luck + calculatedMechanicalItemStats.value.luck,
        maxHp: calculatedNormalItemStats.value.maxHp + calculatedMechanicalItemStats.value.maxHp,
        atk: calculatedNormalItemStats.value.atk + calculatedMechanicalItemStats.value.atk,
        evasion: calculatedNormalItemStats.value.evasion + calculatedMechanicalItemStats.value.evasion,
        critical: calculatedNormalItemStats.value.critical + calculatedMechanicalItemStats.value.critical,
        defense: calculatedNormalItemStats.value.defense + calculatedMechanicalItemStats.value.defense,
      };
      return stats;
    });
    // Computed property for calculated ghost stats
    const calculatedGhostStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      ghostItems.value.forEach((item) => {
        const itemStats = item.bonusStats || item.stats || {};
        stats.stamina += toNumber(itemStats.stamina);
        stats.str += toNumber(itemStats.str);
        stats.int += toNumber(itemStats.int);
        stats.agi += toNumber(itemStats.agi);
        stats.luck += toNumber(itemStats.luck);
        stats.maxHp += toNumber(itemStats.maxHp);
        stats.atk += toNumber(itemStats.atk);
        stats.evasion += toNumber(itemStats.evasion);
        stats.critical += toNumber(itemStats.critical);
        stats.defense += toNumber(itemStats.defense);
      });
      return stats;
    });
    // Computed property for calculated tattoo stats
    const calculatedTattooStats = computed(() => {
      const stats = emptyStats();
      tattoos.value.forEach((tattoo) => {
        addStats(stats, tattoo.stats);
      });
      return stats;
    });
    // Computed property for calculated skill stats
    const calculatedSkillStats = computed(() => {
      const stats = emptyStats();
      skills.value.forEach((skill) => {
        addStats(stats, skill.stats);
      });
      return stats;
    });
    // Computed property for calculated title stats
    const calculatedTitleStats = computed(() => {
      const stats = emptyStats();
      if (currentTitle.value && titles[currentTitle.value]) {
        addStats(stats, titles[currentTitle.value].stats);
      }
      return stats;
    });
    // Computed property to check if itemStats matches calculated stats
    const isItemStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.itemStats) return true;
      return areStatsEqual(playerData.value.itemStats, calculatedItemStats.value);
    });
    // Computed property to check if ghostStats matches calculated ghost stats
    const isGhostStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.ghostStats) return true;
      return areStatsEqual(playerData.value.ghostStats, calculatedGhostStats.value);
    });
    // Computed property to check if tattooStats matches calculated tattoo stats
    const isTattooStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.tattooStats) return true;
      return areStatsEqual(playerData.value.tattooStats, calculatedTattooStats.value);
    });
    // Computed property to check if skillStats matches calculated skill stats
    const isSkillStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.skillStats) return true;
      return areStatsEqual(playerData.value.skillStats, calculatedSkillStats.value);
    });
    // Computed property to check if titleStats matches calculated title stats
    const isTitleStatsMatching = computed(() => {
      if (!playerData.value || !playerData.value.titleStats) return true;
      return areStatsEqual(playerData.value.titleStats, calculatedTitleStats.value);
    });
    // Handle image error
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/64';
    };
    // Format survival time
    const formatSurvivalTime = (timestamp) => {
      if (!timestamp || !timestamp.toDate) return 'ไม่ทราบ';
      try {
        const startTime = dayjs(timestamp.toDate());
        const now = dayjs();
        const duration = now.diff(startTime);
        const days = Math.floor(duration / (1000 * 60 * 60 * 24));
        const hours = Math.floor((duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
        let result = 'รอดมา: ';
        if (days > 0) result += `${days} วัน `;
        if (hours > 0 || days > 0) result += `${hours} ชั่วโมง `;
        result += `${minutes} นาที`;
        return result.trim();
      } catch {
        return 'ไม่ทราบ';
      }
    };
    // Fetch ranked survivors
    const fetchSurvivors = async () => {
      try {
        isLoading.value = true;
        const query = projectFirestore
          .collection('characters')
          .where('isLive', '==', true)
          .where('room', '>=', 5)
          .orderBy('room', 'desc')
          .orderBy('survivalStartTime', 'asc')
          .limit(pageSize);
        const snapshot = await query.get();
        survivors.value = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1] || null;
        hasMoreData.value = snapshot.docs.length === pageSize;
        isLoading.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดอันดับ: ' + err.message;
        isLoading.value = false;
      }
    };
    // Load more survivors
    const loadMoreSurvivors = async () => {
      if (!hasMoreData.value || isLoadingMore.value) return;
      try {
        isLoadingMore.value = true;
        const query = projectFirestore
          .collection('characters')
          .where('isLive', '==', true)
          .where('room', '>=', 5)
          .orderBy('room', 'desc')
          .orderBy('survivalStartTime', 'asc')
          .startAfter(lastDoc.value)
          .limit(pageSize);
        const snapshot = await query.get();
        const newSurvivors = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));
        survivors.value = [...survivors.value, ...newSurvivors];
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1] || null;
        hasMoreData.value = snapshot.docs.length === pageSize;
        isLoadingMore.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลเพิ่ม: ' + err.message;
        isLoadingMore.value = false;
      }
    };
    const computeCardStats = (cardSlots = []) => {
      if (!Array.isArray(cardSlots)) return emptyStats();
      return cardSlots.reduce((sum, slot) => {
        const cs = slot?.cardStats || {};
        Object.keys(cs).forEach((key) => {
          sum[key] = toNumber(sum[key]) + toNumber(cs[key]);
        });
        return sum;
      }, emptyStats());
    };

    // Fetch player data by UID (both target and source)
    const fetchPlayerData = async () => {
      if (!targetUid.value) {
        error.value = 'กรุณากรอก UID ผู้เล่นที่ต้องการตรวจสอบ';
        return;
      }
      selectedCharacter.value = ''; // Clear selected character
      currentIndex.value = survivors.value.findIndex((s) => s.docId === targetUid.value);
      isLoading.value = true;
      error.value = null;
      playerData.value = null;
      sourceData.value = null;
      items.value = [];
      mechanicalItems.value = [];
      tattoos.value = [];
      skills.value = [];
      ghostItems.value = [];
      itemNames.value = [];
      currentTitle.value = null;
      try {
        // Fetch target data
        const targetCharDoc = await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .get();
        if (!targetCharDoc.exists) {
          error.value = 'ไม่พบข้อมูลผู้เล่นสำหรับ UID ที่ต้องการตรวจสอบ';
          isLoading.value = false;
          return;
        }
        const targetCharactersDoc = await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .get();
        if (!targetCharactersDoc.exists) {
          error.value = 'ไม่พบข้อมูลตัวละครสำหรับ UID ที่ต้องการตรวจสอบ';
          isLoading.value = false;
          return;
        }
        const targetData = targetCharDoc.data();
        const targetCharactersData = targetCharactersDoc.data();
        playerData.value = {
          level: targetCharactersData.level || 1,
          silver: targetCharactersData.silver || 0,
          gold: targetCharactersData.gold || 0,
          diamond: targetCharactersData.diamond || 0,
          statPoints: targetData.statPoints || 0,
          itemStats: normalizeStats(targetData.itemStats),
          bonusStats: normalizeStats(targetData.bonusStats),
          stats: normalizeStats(targetData.stats),
          traitStats: normalizeStats(targetData.traitStats),
          skillStats: normalizeStats(targetData.skillStats),
          titleStats: normalizeStats(targetData.titleStats),
          tattooStats: normalizeStats(targetData.tattooStats),
          ghostStats: normalizeStats(targetData.ghostStats),
        };
        currentTitle.value = targetCharactersData.title || null;
        // Normal equipped items from characterPrivate.equippedItems (V2 object map)
        const equippedItemsData = targetData.equippedItems || {};
        if (equippedItemsData && typeof equippedItemsData === 'object' && !Array.isArray(equippedItemsData) && Object.keys(equippedItemsData).length > 0) {
          items.value = Object.entries(equippedItemsData).map(([instanceId, item]) => {
            const tpl = gameItemsData[item.templateId] || {};
            const states = item.states || {};
            const cardStats = computeCardStats(states.cardSlots);
            return {
              id: instanceId,
              instanceId,
              name: tpl.name || item.templateId,
              ...tpl,
              ...states,
              cardStats,
            };
          });
          itemNames.value = items.value.map(item => item.name).filter(Boolean);
        } else {
          items.value = [];
          itemNames.value = [];
        }
        // Mechanical items from characterPrivate.mechanicalItems (V2 object map)
        if (targetData.mechanicalItems && typeof targetData.mechanicalItems === 'object' && !Array.isArray(targetData.mechanicalItems)) {
          mechanicalItems.value = Object.entries(targetData.mechanicalItems).map(([instanceId, item]) => {
            const tpl = gameItemsData[item.templateId] || {};
            const states = item.states || {};
            const cardStats = computeCardStats(states.cardSlots);
            return {
              id: instanceId,
              instanceId,
              name: tpl.name || item.templateId,
              ...tpl,
              ...states,
              cardStats,
            };
          });
        } else {
          mechanicalItems.value = [];
        }
        // Ghost items from characterPrivate.ghostItems (V2 object map)
        if (targetData.ghostItems && typeof targetData.ghostItems === 'object' && !Array.isArray(targetData.ghostItems)) {
          ghostItems.value = Object.entries(targetData.ghostItems).map(([instanceId, item]) => {
            const tpl = gameItemsData[item.templateId] || {};
            const states = item.states || {};
            return {
              id: instanceId,
              instanceId,
              name: tpl.name || item.templateId,
              ...tpl,
              ...states,
            };
          });
        } else {
          ghostItems.value = [];
        }
        // Tattoos (unchanged - separate collection)
        if (targetData.tattooNames && Array.isArray(targetData.tattooNames)) {
          const tattooPromises = targetData.tattooNames.map((id) =>
            projectFirestore.collection('tattoos').doc(id).get()
          );
          const tattooDocs = await Promise.all(tattooPromises);
          tattoos.value = tattooDocs
            .filter((doc) => doc.exists)
            .map((doc) => ({ id: doc.id, ...doc.data() }));
        } else {
          tattoos.value = [];
        }
        // Skills from gameItemsData. Supports both old templateId and new learned skill name storage.
        if (targetData.learnedSkills && Array.isArray(targetData.learnedSkills)) {
          skills.value = targetData.learnedSkills
            .map((idOrName) => findGameItemData(idOrName))
            .filter(Boolean);
        } else {
          skills.value = [];
        }
        // Fetch source data if sourceUid is provided
        if (sourceUid.value && sourceUid.value !== targetUid.value) {
          const sourceCharDoc = await projectFirestore
            .collection('characterPrivate')
            .doc(sourceUid.value)
            .get();
          if (sourceCharDoc.exists) {
            const sourceDataRaw = sourceCharDoc.data();
            sourceData.value = {
              itemStats: normalizeStats(sourceDataRaw.itemStats),
              bonusStats: normalizeStats(sourceDataRaw.bonusStats),
              stats: normalizeStats(sourceDataRaw.stats),
              traitStats: normalizeStats(sourceDataRaw.traitStats),
              skillStats: normalizeStats(sourceDataRaw.skillStats),
              tattooStats: normalizeStats(sourceDataRaw.tattooStats),
              titleStats: normalizeStats(sourceDataRaw.titleStats),
              ghostStats: normalizeStats(sourceDataRaw.ghostStats),
            };
          } else {
            sourceData.value = null;
            error.value = 'ไม่พบข้อมูล Source UID';
          }
        } else {
          sourceData.value = null;
        }
      } catch (err) {
        console.error('Error fetching player data:', err);
        error.value = `เกิดข้อผิดพลาด: ${err.message}`;
      } finally {
        isLoading.value = false;
      }
    };
    // Fetch player data by character selection
    const fetchPlayerDataByCharacter = async () => {
      if (!selectedCharacter.value) {
        playerData.value = null;
        sourceData.value = null;
        items.value = [];
        mechanicalItems.value = [];
        tattoos.value = [];
        skills.value = [];
        ghostItems.value = [];
        itemNames.value = [];
        targetUid.value = '';
        sourceUid.value = '';
        currentIndex.value = -1;
        currentTitle.value = null;
        return;
      }
      targetUid.value = selectedCharacter.value;
      currentIndex.value = survivors.value.findIndex((s) => s.docId === selectedCharacter.value);
      await fetchPlayerData();
    };
    // Navigate to previous character
    const previousCharacter = async () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        targetUid.value = selectedCharacter.value;
        await fetchPlayerData();
      }
    };
    // Navigate to next character
    const nextCharacter = async () => {
      if (currentIndex.value < survivors.value.length - 1) {
        currentIndex.value++;
        selectedCharacter.value = survivors.value[currentIndex.value].docId;
        targetUid.value = selectedCharacter.value;
        await fetchPlayerData();
      }
    };
    // Copy stats from source to target
    const copyStats = async () => {
      if (!targetUid.value || !sourceData.value) {
        error.value = 'ไม่พบข้อมูล Target หรือ Source';
        return;
      }
      if (!confirm(`ยืนยันการคัดลอกค่าพลังจาก ${sourceUid.value} ไปยัง ${targetUid.value}?`)) {
        return;
      }
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            stats: normalizeStats(sourceData.value.stats),
            traitStats: normalizeStats(sourceData.value.traitStats),
            skillStats: normalizeStats(sourceData.value.skillStats),
            tattooStats: normalizeStats(sourceData.value.tattooStats),
            titleStats: normalizeStats(sourceData.value.titleStats),
            itemStats: normalizeStats(sourceData.value.itemStats),
            bonusStats: normalizeStats(sourceData.value.bonusStats),
            ghostStats: normalizeStats(sourceData.value.ghostStats),
          });
        // Update local state
        playerData.value.stats = normalizeStats(sourceData.value.stats);
        playerData.value.traitStats = normalizeStats(sourceData.value.traitStats);
        playerData.value.skillStats = normalizeStats(sourceData.value.skillStats);
        playerData.value.tattooStats = normalizeStats(sourceData.value.tattooStats);
        playerData.value.titleStats = normalizeStats(sourceData.value.titleStats);
        playerData.value.itemStats = normalizeStats(sourceData.value.itemStats);
        playerData.value.bonusStats = normalizeStats(sourceData.value.bonusStats);
        playerData.value.ghostStats = normalizeStats(sourceData.value.ghostStats);
        error.value = 'คัดลอกค่าพลังสำเร็จ';
      } catch (err) {
        console.error('Error copying stats:', err);
        error.value = `เกิดข้อผิดพลาดในการคัดลอก: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update level and reset stats in both collections
    const updateLevel = async () => {
      if (!targetUid.value || !playerData.value || !newLevel.value || newLevel.value <= 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        const parsedLevel = parseInt(newLevel.value, 10);
        const newStatPoints = (parsedLevel - 1) * 5;
        const resetStats = emptyStats(BASE_STAT_KEYS);
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            level: parsedLevel,
            statPoints: newStatPoints,
            stats: resetStats,
          });
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            level: parsedLevel,
          });
        playerData.value.level = parsedLevel;
        playerData.value.statPoints = newStatPoints;
        playerData.value.stats = { ...resetStats };
        newLevel.value = null;
      } catch (err) {
        console.error('Error updating level:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดตเลเวล: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update silver in characters
    const updateSilver = async () => {
      if (!targetUid.value || !playerData.value || !addSilver.value || addSilver.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            silver: firebase.firestore.FieldValue.increment(parseInt(addSilver.value)),
          });
        playerData.value.silver += parseInt(addSilver.value);
        addSilver.value = null;
      } catch (err) {
        console.error('Error updating silver:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต silver: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Subtract silver in characters
    const subtractSilverFunc = async () => {
      if (!targetUid.value || !playerData.value || !subtractSilver.value || subtractSilver.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        const amount = parseInt(subtractSilver.value);
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            silver: firebase.firestore.FieldValue.increment(-amount),
          });
        playerData.value.silver -= amount;
        subtractSilver.value = null;
      } catch (err) {
        console.error('Error subtracting silver:', err);
        error.value = `เกิดข้อผิดพลาดในการลด silver: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update gold in characters
    const updateGold = async () => {
      if (!targetUid.value || !playerData.value || !addGold.value || addGold.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            gold: firebase.firestore.FieldValue.increment(parseInt(addGold.value)),
          });
        playerData.value.gold += parseInt(addGold.value);
        addGold.value = null;
      } catch (err) {
        console.error('Error updating gold:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต gold: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Subtract gold in characters
    const subtractGoldFunc = async () => {
      if (!targetUid.value || !playerData.value || !subtractGold.value || subtractGold.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        const amount = parseInt(subtractGold.value);
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            gold: firebase.firestore.FieldValue.increment(-amount),
          });
        playerData.value.gold -= amount;
        subtractGold.value = null;
      } catch (err) {
        console.error('Error subtracting gold:', err);
        error.value = `เกิดข้อผิดพลาดในการลด gold: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update diamond in characters
    const updateDiamond = async () => {
      if (!targetUid.value || !playerData.value || !addDiamond.value || addDiamond.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            diamond: firebase.firestore.FieldValue.increment(parseInt(addDiamond.value)),
          });
        playerData.value.diamond += parseInt(addDiamond.value);
        addDiamond.value = null;
      } catch (err) {
        console.error('Error updating diamond:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต diamond: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Subtract diamond in characters
    const subtractDiamondFunc = async () => {
      if (!targetUid.value || !playerData.value || !subtractDiamond.value || subtractDiamond.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        const amount = parseInt(subtractDiamond.value);
        await projectFirestore
          .collection('characters')
          .doc(targetUid.value)
          .update({
            diamond: firebase.firestore.FieldValue.increment(-amount),
          });
        playerData.value.diamond -= amount;
        subtractDiamond.value = null;
      } catch (err) {
        console.error('Error subtracting diamond:', err);
        error.value = `เกิดข้อผิดพลาดในการลด diamond: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update itemStats in characterPrivate
    const updateItemStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            itemStats: normalizeStats(calculatedItemStats.value),
          });
        playerData.value.itemStats = normalizeStats(calculatedItemStats.value);
      } catch (err) {
        console.error('Error updating itemStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต itemStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update ghostStats in characterPrivate
    const updateGhostStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            ghostStats: normalizeStats(calculatedGhostStats.value),
          });
        playerData.value.ghostStats = normalizeStats(calculatedGhostStats.value);
      } catch (err) {
        console.error('Error updating ghostStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต ghostStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update tattooStats in characterPrivate
    const updateTattooStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            tattooStats: normalizeStats(calculatedTattooStats.value),
          });
        playerData.value.tattooStats = normalizeStats(calculatedTattooStats.value);
      } catch (err) {
        console.error('Error updating tattooStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต tattooStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update skillStats in characterPrivate
    const updateSkillStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            skillStats: normalizeStats(calculatedSkillStats.value),
          });
        playerData.value.skillStats = normalizeStats(calculatedSkillStats.value);
      } catch (err) {
        console.error('Error updating skillStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต skillStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update titleStats in characterPrivate
    const updateTitleStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            titleStats: normalizeStats(calculatedTitleStats.value),
          });
        playerData.value.titleStats = normalizeStats(calculatedTitleStats.value);
      } catch (err) {
        console.error('Error updating titleStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต titleStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update bonusStats in characterPrivate
    const updateBonusStats = async () => {
      if (!targetUid.value || !playerData.value) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characterPrivate')
          .doc(targetUid.value)
          .update({
            bonusStats: normalizeStats(calculatedBonusStats.value),
          });
        playerData.value.bonusStats = normalizeStats(calculatedBonusStats.value);
      } catch (err) {
        console.error('Error updating bonusStats:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต bonusStats: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Initialize data
    onMounted(async () => {
      await fetchSurvivors();
    });
    return {
      targetUid,
      sourceUid,
      selectedCharacter,
      playerData,
      sourceData,
      items,
      mechanicalItems,
      tattoos,
      skills,
      ghostItems,
      survivors,
      error,
      isLoading,
      isUpdating,
      isLoadingMore,
      hasMoreData,
      currentIndex,
      newLevel,
      addSilver,
      addGold,
      addDiamond,
      subtractSilver,
      subtractGold,
      subtractDiamond,
      itemNames,
      currentTitle,
      calculatedItemStats,
      calculatedNormalItemStats,
      calculatedMechanicalItemStats,
      isItemStatsMatching,
      calculatedGhostStats,
      isGhostStatsMatching,
      calculatedTattooStats,
      isTattooStatsMatching,
      calculatedSkillStats,
      isSkillStatsMatching,
      calculatedTitleStats,
      isTitleStatsMatching,
      calculatedBonusStats,
      isBonusStatsMatching,
      handleImageError,
      formatSurvivalTime,
      fetchPlayerData,
      fetchPlayerDataByCharacter,
      previousCharacter,
      nextCharacter,
      copyStats,
      updateLevel,
      updateSilver,
      subtractSilverFunc,
      updateGold,
      subtractGoldFunc,
      updateDiamond,
      subtractDiamondFunc,
      updateItemStats,
      updateGhostStats,
      updateTattooStats,
      updateSkillStats,
      updateTitleStats,
      updateBonusStats,
      loadMoreSurvivors,
    };
  },
};
</script>
<style scoped>
/* Background */
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1')
      no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
/* Button Styles */
.btn {
  position: relative;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #e0e7ff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Orbitron', sans-serif;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  background-size: 200% 100%;
}
.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
}
.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
  z-index: 0;
}
.btn:hover::before {
  left: 100%;
}
.btn-primary {
  background-color: #1f2937;
  border: 1px solid #374151;
}
.btn-primary:hover {
  background-color: #111827;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(17, 24, 39, 0.5);
}
.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(17, 24, 39, 0.3);
}
.btn:disabled {
  background: #4b5563;
  transform: none;
  box-shadow: none;
  cursor: not-allowed;
}
.btn i {
  transition: transform 0.3s ease;
}
.btn:hover i {
  transform: scale(1.1);
}
/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}
@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}
@keyframes spin-pulse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.animate-spin-pulse {
  animation: spin-pulse 1s infinite linear;
}
/* Font */
.font-orbitron {
  font-family: 'Orbitron', sans-serif;
}
/* Responsive */
@media (max-width: 639px) {
  .text-lg {
    font-size: 1rem;
  }
  .text-xl {
    font-size: 1.125rem;
  }
  .text-sm {
    font-size: 0.75rem;
  }
  .p-5 {
    padding: 0.75rem;
  }
}
</style>